import { useState, type ReactNode } from 'react';

import { CARD_LINK } from '@/components/agent-chat/agent-card.component';
import {
  CardRow,
  CardRows,
} from '@/components/agent-chat/card-parts.component';
import type { ConfirmationRequest } from '@/hooks/use-agent-chat.hook';

/** Clu's one change to a record in a connected tool, confirmed on its card. */
export const CONNECTION_API_WRITE_ACTION = 'connection_api_write';

/** A row's value as text: a string or a number, else empty. */
function shown(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' ? value.trim() : '';
}

/** Only a web link opens from the card. */
function webLink(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.href
      : null;
  } catch {
    return null;
  }
}

/** One row's value: a field change as old then new, else its value, a
 *  record's as a link that opens it. Null for a row it can't read. */
function rowEnd(row: Record<string, unknown>): ReactNode | null {
  if ('old' in row || 'new' in row) return change(row.old, row.new);
  const value = shown(row.value);
  if (!value) return null;
  return linked(value, row.link);
}

/** A field's old value struck through, then its new one. */
function change(before: unknown, after: unknown): ReactNode {
  return (
    <span className='inline-flex flex-wrap items-center justify-end gap-x-[6px]'>
      <span className='line-through'>{shown(before) || 'Empty'}</span>
      <span aria-hidden>→</span>
      <span className='font-medium text-foreground'>
        {shown(after) || 'Empty'}
      </span>
    </span>
  );
}

/** Text that opens its link in a new tab, when the link is a web one. */
function linked(text: string, link: unknown): ReactNode {
  const href = webLink(link);
  return href ? (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className={CARD_LINK}
    >
      {text}
    </a>
  ) : (
    text
  );
}

/** A bulk card's records or left out ones: each with its name, never junk. */
function recordsOf(value: unknown): Record<string, unknown>[] {
  return (Array.isArray(value) ? value : []).flatMap((item: unknown) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const name = shown(record.name) || shown(record.id);
    return name ? [{ ...record, name }] : [];
  });
}

/**
 * The records a bulk card pins past the first ones its rows already show
 * (`skip`), and the ones it leaves out with why, behind Show all.
 */
function AllRecords({
  args,
  skip,
}: {
  args: Record<string, unknown>;
  skip: number;
}) {
  const [open, setOpen] = useState(false);
  const records = recordsOf(args.records).slice(skip);
  const leftOut = recordsOf(args.left_out);
  if (!records.length && !leftOut.length) return null;
  return (
    <div className='flex flex-col gap-[4px]'>
      <button
        type='button'
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={`${CARD_LINK} self-start`}
      >
        {open ? 'Show less' : 'Show all'}
      </button>
      {open ? (
        <div
          className='flex flex-col gap-[8px]'
          data-testid='connection-api-write-all'
        >
          {records.length ? (
            <CardRows label='Records'>
              {records.map((record, index) => (
                <CardRow
                  key={`${index}-${shown(record.id)}`}
                  label={linked(record.name as string, record.url)}
                  end={change(record.old, record.new)}
                  wrap
                />
              ))}
            </CardRows>
          ) : null}
          {leftOut.length ? (
            <CardRows label='Left out'>
              {leftOut.map((record, index) => (
                <CardRow
                  key={`${index}-${shown(record.id)}`}
                  label={record.name as string}
                  end={shown(record.reason) || undefined}
                  wrap
                />
              ))}
            </CardRows>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The change a connection write card asks to make, from the rows the server
 * built at card time: the connection, the record, its facts, look-alikes,
 * and each field's old and new value. Null for any other card.
 */
export function ConnectionApiWritePreview({
  request,
}: {
  request: ConfirmationRequest;
}) {
  if (request.tool_name !== CONNECTION_API_WRITE_ACTION) return null;
  const args = request.arguments ?? {};
  const rows = (Array.isArray(args.rows) ? args.rows : []).flatMap(
    (row: unknown, index) => {
      if (!row || typeof row !== 'object') return [];
      const record = row as Record<string, unknown>;
      const label = shown(record.label);
      const end = label ? rowEnd(record) : null;
      // A bulk card's record row opens its record from its name.
      const changed = 'old' in record || 'new' in record;
      const name = changed ? linked(label, record.link) : label;
      return end === null
        ? []
        : [{ key: `${index}-${label}`, label: name, end, changed }];
    }
  );
  const bulk = Array.isArray(args.record_ids);
  if (!rows.length && !bulk) return null;
  // A bulk card's rows hold its first records, in order, old and new.
  const inRows = rows.filter((row) => row.changed).length;
  return (
    <div
      className='flex flex-col gap-[8px]'
      data-testid='connection-api-write-preview'
    >
      {rows.length ? (
        <CardRows label='Change'>
          {rows.map((row) => (
            <CardRow key={row.key} label={row.label} end={row.end} wrap />
          ))}
        </CardRows>
      ) : null}
      {bulk ? <AllRecords args={args} skip={inRows} /> : null}
    </div>
  );
}
